import React, { useEffect, useState } from "react";
import { Lock, Plus, Copy, Check, LogOut, FileText, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("altamira_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [estimates, setEstimates] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState({ cliente: "", empresa: "", correo: "", concepto: "", monto: "" });
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState("");
  const [chats, setChats] = useState([]);
  const [openChat, setOpenChat] = useState("");
  const [chatMsgs, setChatMsgs] = useState([]);

  const authFetch = (path, opts = {}) =>
    fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });

  const load = async (tk = token) => {
    const h = { Authorization: `Bearer ${tk}` };
    const [er, qr, cr] = await Promise.all([
      fetch(`${API}/api/estimates`, { headers: h }),
      fetch(`${API}/api/contact`, { headers: h }),
      fetch(`${API}/api/admin/chats`, { headers: h }),
    ]);
    if (er.status === 401) { logout(); return; }
    setEstimates(await er.json());
    if (qr.ok) setQuotes(await qr.json());
    if (cr.ok) setChats(await cr.json());
  };

  useEffect(() => { if (token) load(); }, []); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    setError("");
    const res = await fetch(`${API}/api/admin/login`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) { setError("Contraseña incorrecta"); return; }
    const { token: tk } = await res.json();
    localStorage.setItem("altamira_admin_token", tk);
    setToken(tk);
    load(tk);
  };

  const logout = () => { localStorage.removeItem("altamira_admin_token"); setToken(""); };

  const createEstimate = async (e) => {
    e.preventDefault();
    const monto = parseFloat(form.monto);
    if (!form.cliente.trim() || !form.concepto.trim() || !(monto > 0)) { setError("Complete cliente, concepto y un monto válido"); return; }
    setError("");
    setCreating(true);
    const res = await authFetch("/api/estimates", { method: "POST", body: JSON.stringify({ ...form, monto }) });
    setCreating(false);
    if (res.status === 401) { logout(); return; }
    if (!res.ok) { setError("No se pudo crear el presupuesto"); return; }
    setForm({ cliente: "", empresa: "", correo: "", concepto: "", monto: "" });
    load();
  };

  const copyLink = async (id) => {
    const link = `${window.location.origin}/presupuesto/${id}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1800);
    } catch {
      window.prompt("Copie el enlace manualmente:", link);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const prefillFromQuote = (q) => {
    setForm({
      cliente: q.nombre || "",
      empresa: q.empresa || "",
      correo: q.correo || "",
      concepto: [q.categoria, q.mensaje].filter(Boolean).join(": ").slice(0, 160),
      monto: "",
    });
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleChat = async (sid) => {
    if (openChat === sid) { setOpenChat(""); return; }
    setOpenChat(sid);
    setChatMsgs([]);
    const res = await fetch(`${API}/api/chat/${sid}`);
    if (res.ok) setChatMsgs(await res.json());
  };

  if (!token) {
    return (
      <section className="section" style={{ paddingTop: 160, minHeight: "70vh" }}>
        <div className="container-x" style={{ maxWidth: 420 }}>
          <div className="card" style={{ padding: 32 }}>
            <h2 style={{ marginBottom: 16 }}><Lock size={20} style={{ display: "inline", verticalAlign: "-3px", marginRight: 8 }} />Panel Admin</h2>
            <form onSubmit={login} className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
              <div className="field"><label>Contraseña</label>
                <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
              </div>
              {error && <p data-testid="admin-login-error" style={{ color: "#c0392b", fontSize: 14 }}>{error}</p>}
              <button data-testid="admin-login-button" type="submit" className="btn btn-navy">Entrar</button>
            </form>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section" style={{ paddingTop: 140, minHeight: "80vh" }} data-testid="admin-dashboard">
      <div className="container-x">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <h1 style={{ fontSize: 34 }}>Panel de administración</h1>
          <button data-testid="admin-logout-button" onClick={logout} className="btn btn-outline"><LogOut size={15} /> Salir</button>
        </div>

        <div className="card" style={{ padding: 26, marginTop: 26 }}>
          <h3><Plus size={18} style={{ display: "inline", verticalAlign: "-3px", marginRight: 6 }} />Nuevo presupuesto</h3>
          <form onSubmit={createEstimate} noValidate className="form-grid" style={{ marginTop: 14 }}>
            <div className="field"><label>Cliente *</label><input data-testid="estimate-cliente-input" value={form.cliente} onChange={upd("cliente")} /></div>
            <div className="field"><label>Empresa</label><input data-testid="estimate-empresa-input" value={form.empresa} onChange={upd("empresa")} /></div>
            <div className="field"><label>Correo del cliente</label><input data-testid="estimate-correo-input" type="email" value={form.correo} onChange={upd("correo")} /></div>
            <div className="field"><label>Monto (USD) *</label><input data-testid="estimate-monto-input" type="number" min="0.5" step="0.01" value={form.monto} onChange={upd("monto")} placeholder="1500.00" /></div>
            <div className="field full"><label>Concepto *</label><input data-testid="estimate-concepto-input" value={form.concepto} onChange={upd("concepto")} placeholder="Ej: 12 overoles ignífugos Nomex talla L con logo" /></div>
            {error && <p className="full" data-testid="estimate-error" style={{ color: "#c0392b", fontSize: 14 }}>{error}</p>}
            <div className="full">
              <button data-testid="estimate-create-button" type="submit" disabled={creating} className="btn btn-navy">
                {creating ? "Creando..." : "Crear y generar enlace de pago"}
              </button>
            </div>
          </form>
        </div>

        <h3 style={{ marginTop: 44 }}><FileText size={18} style={{ display: "inline", verticalAlign: "-3px", marginRight: 6 }} />Presupuestos ({estimates.length})</h3>
        <div data-testid="estimate-list" style={{ display: "grid", gap: 12, marginTop: 14 }}>
          {estimates.length === 0 && <p style={{ color: "var(--muted)" }}>Aún no hay presupuestos.</p>}
          {estimates.map((es) => (
            <div key={es.id} className="card" data-testid="estimate-row" style={{ padding: 18, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ minWidth: 220 }}>
                <b>{es.cliente}</b>{es.empresa ? ` — ${es.empresa}` : ""}
                <div style={{ fontSize: 13, color: "var(--muted)" }}>{es.concepto}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>{new Date(es.created_at).toLocaleString()}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <b style={{ fontSize: 18 }}>${Number(es.monto).toFixed(2)}</b>
                <span data-testid="estimate-status" className="chip" style={{ background: es.status === "paid" ? "#e7f8ee" : "#fdf3e0", color: es.status === "paid" ? "#1e8e4e" : "#9a6b12", fontWeight: 700 }}>
                  {es.status === "paid" ? "PAGADO" : "PENDIENTE"}
                </span>
                <button data-testid="copy-link-button" onClick={() => copyLink(es.id)} className="btn btn-outline" style={{ padding: "8px 14px" }}>
                  {copiedId === es.id ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar enlace</>}
                </button>
              </div>
            </div>
          ))}
        </div>

        <h3 style={{ marginTop: 44 }}>Solicitudes de cotización recibidas ({quotes.length})</h3>
        <div data-testid="quotes-list" style={{ display: "grid", gap: 12, marginTop: 14 }}>
          {quotes.length === 0 && <p style={{ color: "var(--muted)" }}>Sin solicitudes por ahora.</p>}
          {quotes.map((q) => (
            <div key={q.id} className="card" data-testid="quote-row" style={{ padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <b>{q.nombre}{q.empresa ? ` — ${q.empresa}` : ""}</b>
                  <span data-testid="quote-channel" className="chip" style={{ fontSize: 10, fontWeight: 700, background: q.channel === "chat" ? "#eef2ff" : "#f1f5f9", color: q.channel === "chat" ? "#4338ca" : "#475569" }}>
                    {q.channel === "chat" ? "CHAT IA" : q.channel === "wa" ? "WHATSAPP" : "FORMULARIO"}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: "var(--muted)" }}>{new Date(q.created_at).toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
                {q.telefono && <span>{q.telefono} · </span>}{q.correo && <span>{q.correo} · </span>}{q.ciudad && <span>{q.ciudad} · </span>}{q.categoria}
              </div>
              <p style={{ fontSize: 14, marginTop: 8 }}>{q.mensaje}</p>
              <button data-testid="quote-to-estimate-button" onClick={() => prefillFromQuote(q)} className="btn btn-outline" style={{ marginTop: 10, padding: "8px 14px", fontSize: 13 }}>
                <Plus size={14} /> Crear presupuesto
              </button>
            </div>
          ))}
        </div>

        <h3 style={{ marginTop: 44 }}><MessageSquare size={18} style={{ display: "inline", verticalAlign: "-3px", marginRight: 6 }} />Conversaciones del asistente ({chats.length})</h3>
        <div data-testid="chat-history-list" style={{ display: "grid", gap: 12, marginTop: 14 }}>
          {chats.length === 0 && <p style={{ color: "var(--muted)" }}>Aún no hay conversaciones.</p>}
          {chats.map((c) => (
            <div key={c.session_id} className="card" data-testid="chat-session-row" style={{ padding: 18 }}>
              <button data-testid="chat-session-toggle" onClick={() => toggleChat(c.session_id)} style={{ all: "unset", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <b style={{ fontSize: 14 }}>"{c.preview}"</b>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>
                    {c.count} mensajes · {new Date(c.last_at).toLocaleString()}
                  </div>
                </div>
                {openChat === c.session_id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              {openChat === c.session_id && (
                <div data-testid="chat-session-messages" style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, maxHeight: 380, overflowY: "auto", background: "#f6f8fb", borderRadius: 12, padding: 14 }}>
                  {chatMsgs.length === 0 && <p style={{ fontSize: 13, color: "var(--muted)" }}>Cargando...</p>}
                  {chatMsgs.map((m) => (
                    <div key={m.id} style={{
                      alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                      maxWidth: "85%", padding: "8px 12px", borderRadius: 12, fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap",
                      background: m.role === "user" ? "var(--navy)" : "#fff",
                      color: m.role === "user" ? "#fff" : "var(--navy)",
                      border: m.role === "user" ? "none" : "1px solid rgba(11,31,58,0.08)",
                    }}>
                      {m.content}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
